import React, { useState, useMemo, useEffect } from "react";
import { ActivityFilter } from "./ActivityCards";
import { formatDate, formatDateOnly, formatTime, formatTemperature, formatVolume } from "./utils/roomUtils";

const COLORS = {
  heartbeat: "#C68A6A",
  douche: "#6C8EBF",
  pasActivite: "rgba(27, 36, 27, 0.4)",
  moduleWarning: "#DCCD2C",
  good: "#96B77A",
  batterie: "#FF8B8B",
  moduleAssociated: "#6366F1",
  moduleDissociated: "#9CA3AF",
};

const PAGE_SIZE = 100;

const ActivityTable = ({ heartbeats = [], showers = [], statusLogs = [], moduleLogs = [] }) => {
  const [selectedTypes, setSelectedTypes] = useState(["heartbeat", "shower", "status_log", "module_event", "pas_activite"]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const getDayKey = (tsOrDate) => {
    if (!tsOrDate) return "";
    const d = new Date(tsOrDate);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  // Activités API + lignes "Pas d'activité" pour les jours vides, tri par date décroissante
  const tableRows = useMemo(() => {
    const list = [];
    heartbeats.forEach((h) => {
      list.push({
        rowType: "heartbeat",
        activity: {
          type: "heartbeat",
          timestamp: h.created_at,
          data: {
            id: h.id,
            batteryLevel: h.batteryLvl,
            rssi: h.RSSI,
            snr: h.SNR,
            type: h.type,
            config_id: h.config_id ?? null,
            is_tablette: h.is_tablette ?? 0,
          },
        },
      });
    });
    showers.forEach((s) => {
      list.push({
        rowType: "shower",
        activity: {
          type: "shower",
          timestamp: s.shower_created_at || s.created_at,
          data: {
            id: s.shower_id,
            volume: s.volume,
            subVolume: s.subVolume ?? null,
            temperature: s.temperature,
            objective: s.objective,
            interaction: s.interaction,
            shower_timestamp: s.shower_timestamp || null,
          },
        },
      });
    });
    statusLogs.forEach((log) => {
      list.push({
        rowType: "status_log",
        activity: {
          type: "status_log",
          timestamp: log.date,
          data: {
            oldStatus: log.old_status,
            newStatus: log.new_status,
            origin: log.origin,
          },
        },
      });
    });
    moduleLogs.forEach((log) => {
      list.push({
        rowType: "module_event",
        activity: {
          type: log.type, // "module_associated" | "module_dissociated"
          timestamp: log.timestamp,
          data: {
            module_id: log.module_id,
            module_number: log.module_number,
          },
        },
      });
    });
    const daysWithActivity = new Set(list.map((r) => getDayKey(r.activity.timestamp)));
    let minTs = Infinity;
    let maxTs = -Infinity;
    list.forEach((r) => {
      const t = new Date(r.activity.timestamp).getTime();
      if (t < minTs) minTs = t;
      if (t > maxTs) maxTs = t;
    });
    if (list.length > 0 && minTs !== Infinity) {
      const minD = new Date(minTs);
      const maxD = new Date(maxTs);
      for (let d = new Date(minD.getFullYear(), minD.getMonth(), minD.getDate()); d <= maxD; d.setDate(d.getDate() + 1)) {
        const key = getDayKey(d);
        if (!daysWithActivity.has(key)) {
          list.push({
            rowType: "pas_activite",
            activity: {
              type: "pas_activite",
              timestamp: new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString(),
              date: key,
              data: {},
            },
          });
        }
      }
    }
    const filtered = list.filter((r) => selectedTypes.includes(r.rowType));
    filtered.sort((a, b) => new Date(b.activity.timestamp) - new Date(a.activity.timestamp));
    return filtered;
  }, [heartbeats, showers, statusLogs, moduleLogs, selectedTypes]);

  const toggleTypeFilter = (type) => {
    setSelectedTypes((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));
  };
  const clearAllFilters = () => setSelectedTypes([]);
  const selectAllFilters = () => setSelectedTypes(["heartbeat", "shower", "status_log", "pas_activite"]);

  const pasActiviteCount = useMemo(() => {
    const list = [];
    heartbeats.forEach((h) => list.push(new Date(h.created_at)));
    showers.forEach((s) => list.push(new Date(s.shower_created_at || s.created_at)));
    statusLogs.forEach((log) => list.push(new Date(log.date)));
    moduleLogs.forEach((log) => list.push(new Date(log.timestamp)));
    if (list.length === 0) return 0;
    const minD = new Date(Math.min(...list.map((d) => d.getTime())));
    const maxD = new Date(Math.max(...list.map((d) => d.getTime())));
    const daysSet = new Set(list.map((d) => getDayKey(d)));
    let count = 0;
    for (let d = new Date(minD.getFullYear(), minD.getMonth(), minD.getDate()); d <= maxD; d.setDate(d.getDate() + 1)) {
      if (!daysSet.has(getDayKey(d))) count++;
    }
    return count;
  }, [heartbeats, showers, statusLogs, moduleLogs]);

  const activityTypes = [
    { key: "heartbeat", label: "Heartbeats", color: "bg-[#C68A6A] text-white", count: heartbeats.length },
    { key: "shower", label: "Douches", color: "bg-[#6C8EBF] text-white", count: showers.length },
    { key: "status_log", label: "Logs de statut", color: "bg-[#DCCD2C] text-white", count: statusLogs.length },
    { key: "module_event", label: "Modules", color: "bg-[#6366F1] text-white", count: moduleLogs.length },
    { key: "pas_activite", label: "Pas d'activité", color: "bg-[rgba(27,36,27,0.4)] text-gray-900", count: pasActiviteCount },
  ];

  const renderTypeCell = (row) => {
    const a = row.activity;
    if (a.type === "pas_activite") {
      return (
        <span className="px-3 py-1 rounded-full text-sm font-medium text-white" style={{ backgroundColor: COLORS.pasActivite }}>
          Pas d'activité
        </span>
      );
    }
    if (a.type === "heartbeat") {
      return (
        <span className="inline-flex items-end gap-2">
          <span className="px-3 py-1 rounded-full text-[13px] font-semibold text-white" style={{ backgroundColor: COLORS.heartbeat }}>
            Heartbeat
          </span>
          {a.data.id && (
            <span className="text-[11px] text-gray-500 pb-0.5">#{a.data.id}</span>
          )}
        </span>
      );
    }
    if (a.type === "shower") {
      return (
        <span className="inline-flex items-end gap-2">
          <span className="px-3 py-1 rounded-full text-[13px] font-semibold text-white" style={{ backgroundColor: COLORS.douche }}>
            Douche
          </span>
          {a.data.id && (
            <span className="text-[11px] text-gray-500 pb-0.5">#{a.data.id}</span>
          )}
        </span>
      );
    }
    if (a.type === "module_associated" || a.type === "module_dissociated") {
      const isAssoc = a.type === "module_associated";
      return (
        <span className="inline-flex items-center gap-2">
          <span
            className="px-3 py-1 rounded-full text-[13px] font-semibold text-white"
            style={{ backgroundColor: isAssoc ? COLORS.moduleAssociated : COLORS.moduleDissociated }}
          >
            {isAssoc ? "Module associé" : "Module dissocié"}
          </span>
          {a.data.module_number != null && (
            <span className="text-[11px] text-gray-500 pb-0.5">#{a.data.module_number}</span>
          )}
        </span>
      );
    }
    if (a.type === "status_log") {
      const getStatusStyle = (status) => {
        if (status === "Good") return { backgroundColor: COLORS.good, color: "white" };
        if (status === "Warning") return { backgroundColor: COLORS.moduleWarning, color: "white" };
        return { backgroundColor: "#E5E7EB", color: "#111827" };
      };
      const oldLabel = a.data.oldStatus === "Warning" ? "Module Warning" : a.data.oldStatus || "N/A";
      const newLabel = a.data.newStatus === "Warning" ? "Module Warning" : a.data.newStatus || "N/A";
      return (
        <span className="inline-flex items-center gap-1 flex-wrap">
          <span className="px-3 py-1 rounded-full text-sm font-medium" style={getStatusStyle(a.data.oldStatus)}>
            {oldLabel}
          </span>
          <span className="text-black text-xl font-bold">→</span>
          <span className="px-3 py-1 rounded-full text-sm font-medium" style={getStatusStyle(a.data.newStatus)}>
            {newLabel}
          </span>
        </span>
      );
    }
    return null;
  };

  /** Retourne les 4 colonnes de détails pour une ligne (contenu centré). */
  const getDetailsColumns = (row) => {
    const a = row.activity;
    const empty = [null, null, null, null];
    if (a.type === "pas_activite" || a.type === "status_log") return empty;
    if (a.type === "heartbeat") {
      const typeLabel = a.data.type ? String(a.data.type).charAt(0).toUpperCase() + String(a.data.type).slice(1) : "";
      const batteryLevel = a.data.batteryLevel != null ? Number(a.data.batteryLevel) : null;
      return [
        typeLabel ? (
          <span className="px-2 py-0.5 rounded-full text-sm font-medium text-black bg-gray-50 border border-gray-200 inline-block">
            {typeLabel}
          </span>
        ) : null,
        batteryLevel != null ? (
          <span
            className="px-2 py-0.5 rounded-full text-sm font-medium text-black inline-block"
            style={
              batteryLevel < 3300
                ? { backgroundColor: COLORS.batterie, color: "white" }
                : { backgroundColor: "transparent", border: "1px solid #D1D5DB" }
            }
          >
            Batterie {batteryLevel.toFixed(1)} mV
          </span>
        ) : null,
        a.data.rssi != null ? <span className="text-black">RSSI {a.data.rssi} dBm</span> : null,
        a.data.is_tablette === 1 ? <span className="text-black font-medium">Tablette connecté</span> : null,
      ];
    }
    if (a.type === "shower") {
      return [
        a.data.volume != null ? (
          <span className="px-2 py-0.5 rounded-full text-sm font-medium text-black bg-gray-50 border border-gray-200 inline-block">
            {a.data.subVolume != null
              ? `Conso ${formatVolume(a.data.volume)} / subVol ${formatVolume(a.data.subVolume)}`
              : `Conso ${formatVolume(a.data.volume)}`}
          </span>
        ) : null,
        a.data.temperature != null ? <span className="text-black">Temp {formatTemperature(a.data.temperature)}</span> : null,
        a.data.objective != null ? <span className="text-black">Objectif {formatVolume(a.data.objective)}</span> : null,
        a.data.interaction ? <span className="text-black font-medium">Tablette connecté</span> : null,
      ];
    }
    return empty;
  };

  const totalPages = Math.max(1, Math.ceil(tableRows.length / PAGE_SIZE));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const visibleRows = useMemo(() => {
    if (!tableRows.length) return [];
    const start = (currentPage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return tableRows.slice(start, end);
  }, [tableRows, currentPage]);

  return (
    <div className="bg-white rounded-lg shadow overflow-visible">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Activité ({tableRows.length})</h2>
          <ActivityFilter
            showFilterModal={showFilterModal}
            setShowFilterModal={setShowFilterModal}
            selectedTypes={selectedTypes}
            toggleTypeFilter={toggleTypeFilter}
            clearAllFilters={clearAllFilters}
            selectAllFilters={selectAllFilters}
            activityTypes={activityTypes}
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date/Heure</th>
              <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type d'activité</th>
              <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">N° config</th>
              <th colSpan={4} className="pl-8 pr-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Détails de l'activité</th>
              <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Heure interne</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {visibleRows.map((row, idx) => {
              const prevRow = visibleRows[idx - 1];
              const thisDay = row.activity.type === "pas_activite" ? row.activity.date : getDayKey(row.activity.timestamp);
              const prevDay = prevRow
                ? prevRow.activity.type === "pas_activite"
                  ? prevRow.activity.date
                  : getDayKey(prevRow.activity.timestamp)
                : null;
              const isFirstOfDay = !prevDay || thisDay !== prevDay;
              const isPasActivite = row.activity.type === "pas_activite";
              const isResetRow =
                row.activity.type === "heartbeat" &&
                (row.activity.data.config_id === null || row.activity.data.config_id === undefined) &&
                String(row.activity.data.type).toUpperCase() === "DEBUG";
              return (
                <tr
                  key={isPasActivite ? `pas_activite-${row.activity.date}` : `${row.rowType}-${row.activity.timestamp}-${idx}`}
                  className={`hover:bg-gray-50 ${
                    isResetRow ? "bg-red-100 text-red-800" : ""
                  }`}
                >
                  <td className="px-6 py-2 whitespace-nowrap text-sm text-black">
                    {isPasActivite ? (
                      <span className="font-semibold">{formatDateOnly(row.activity.date)}</span>
                    ) : (
                      <span className="inline-flex items-baseline">
                        <span className="font-semibold min-w-[10ch]">
                          {isFirstOfDay ? formatDateOnly(row.activity.timestamp) : "\u00A0"}
                        </span>
                        <span className="font-semibold">{formatTime(row.activity.timestamp)}</span>
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-2 whitespace-nowrap text-sm">{renderTypeCell(row)}</td>
                  <td className="px-6 py-2 whitespace-nowrap text-sm text-black">
                    {row.activity.type === "heartbeat"
                      ? row.activity.data.config_id === null || row.activity.data.config_id === undefined
                        ? "NULL"
                        : row.activity.data.config_id
                      : ""}
                  </td>
                  {getDetailsColumns(row).map((cell, colIdx) => (
                    <td key={colIdx} className="px-4 py-2 text-sm text-black text-center align-middle">
                      {cell}
                    </td>
                  ))}
                  <td className="px-6 py-2 whitespace-nowrap text-sm text-black">
                    {row.activity.type === "shower" && row.activity.data.shower_timestamp
                      ? formatDate(row.activity.data.shower_timestamp)
                      : ""}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="px-6 py-3 flex justify-between items-center gap-3">
        <span className="text-xs text-gray-500">
          {tableRows.length} activités au total
        </span>
        <div className="flex items-center gap-3 text-xs text-gray-700">
          <button
            type="button"
            className="px-2 py-1 rounded-full disabled:opacity-50"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            ◀
          </button>
          <span className="text-gray-500">
            Page <span className="font-semibold text-gray-900">{currentPage}</span> / {totalPages}
          </span>
          <button
            type="button"
            className="px-2 py-1 rounded-full disabled:opacity-50"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            ▶
          </button>
        </div>
      </div>

      {tableRows.length === 0 && (
        <div className="px-6 py-4 text-center text-gray-500">
          <p className="text-sm">Aucune activité avec les filtres sélectionnés.</p>
        </div>
      )}
    </div>
  );
};

export default ActivityTable;
